import React, { useState, useEffect } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { dynamicWebFormPreviewDataAtom, flowObjAtom } from '../../../Services/_states/index.js';
import { Node } from '../../../interfaces/Flowbuilder.interface.ts';
import { getAllParentNodeResults } from '../helper.tsx';
import { LeadFormActions } from '../../../Services/_actions/index.js';
import SearchableDropdown from '../Common/SearchableDropdown/SearchableDropdown.tsx';
import { Box, Divider, Typography } from '@mui/material';
import CampaignAssignedDropdown from '../../Shared/CampaignAssignmentDropdown/index.tsx';
import { useSnackBar } from '../Common/SnackBar/index.tsx';
import LeadQueryParameters from './LeadQueryParameters.tsx'; // <-- Import your QueryParameter component
import { staticColumnsMatch } from '../../Leads/DynamicFormFields/helper.ts';

interface LeadDataSettingProps {
  data: Node;
  leadDataObj: any;
  setLeadDataObj: React.Dispatch<React.SetStateAction<any>>;
  leadDataSettingErrors: any;
}

const LeadDataSetting: React.FC<LeadDataSettingProps> = ({
  data,
  leadDataObj,
  setLeadDataObj,
  leadDataSettingErrors,
}) => {
  const { showSnackBar } = useSnackBar();
  const actions = LeadFormActions();
  const [webFormList, setWebFormList] = useRecoilState(dynamicWebFormPreviewDataAtom);
  const [formLoading, setFormLoading] = useState(false);
  const flowObj = useRecoilValue(flowObjAtom);
  const parentResults = getAllParentNodeResults(data?.id ?? null, flowObj);
  const [currentWebFormFields, setCurrentWebFormFields] = useState<any[]>([]);
  const [typeOfStatusList, setTypeOfStatusList] = useState<any>({});
  const [statusList, setStatusList] = useState<any>([]);
  const [isStatusLoading, setIsStatusLoading] = useState(false);

  // const extractAllNodeIds = (group: any): string[] => {
  //     let ids: string[] = [];
  //     if (!group) return ids;

  //     // Collect node_id from conditions
  //     (group.conditions || []).forEach((condition: any) => {
  //         if (condition?.node_id) ids.push(condition.node_id);
  //     });

  //     // Recursively collect from subGroups
  //     (group.subGroups || []).forEach((subGroup: any) => {
  //         ids = ids.concat(extractAllNodeIds(subGroup));
  //     });

  //     return [...new Set(ids)]; // remove duplicates
  // };

  const modifyWebFormListObj = (obj: any) => {
    try {
      if (!obj || Object.keys(obj).length === 0) return [];
      return Object.values(obj);
    } catch (error) {
      console.warn(error);
      return [];
    }
  };

  const getWebFormListByCampaign = async (campaign: any, forceRefresh = false) => {
    try {
      if (!campaign?.proposal_id) return null;
      if (!forceRefresh && webFormList?.[campaign.proposal_id]) {
        return webFormList[campaign.proposal_id];
      }
      const response = await actions.getFormsByCampaignId(campaign.proposal_id);
      if (response?.status === 200) {
        const forms = response?.data ?? null;
        setWebFormList((prev: any) => ({
          ...(prev || {}),
          [campaign.proposal_id]: forms,
        }));
        return forms;
      }
    } catch (error) {
      console.warn('error', error);
    }
    return null;
  };
  // const handleSelectParentResult = async () => {
  //     try {
  //         const nodeIds = extractAllNodeIds(leadDataObj);
  //         if (!nodeIds.length) return;

  //         const nodes = flowObj?.nodes ?? [];

  //         // Avoid repeated API calls for already cached nodes
  //         const nodesToFetch = nodeIds.filter((id) => !webFormList?.[id]);

  //         if (!nodesToFetch.length) return; // All cached

  //         for (const nodeId of nodesToFetch) {
  //             const nodeData: any = nodes.find((node) => node?.id === nodeId);
  //             if (!nodeData?.data?.lead_data_setting) continue;
  //             const campaign = nodeData.data.lead_data_setting.campaign;
  //             const webForm = nodeData.data.lead_data_setting.webForm;
  //             if (!campaign?.proposal_id || !webForm?.leads_form_id) continue;
  //             const response = await actions.getFormsByCampaignId(campaign.proposal_id);
  //             if (response?.status === 200) {
  //                 const forms = response?.data ?? {};
  //                 const targetForm = forms[webForm?.leads_form_id];
  //                 const formItems = Object.values(targetForm?.leads_form_items || [])?.map((item: any) => ({
  //                     key_name: item?.key_name,
  //                     alias_name: item?.alias_name,
  //                     ...(item?.statusOption && { statusOption: item?.statusOption }),
  //                 }));
  //                 if (targetForm) {
  //                     setWebFormList((prev: any) => ({
  //                         ...(prev || {}),
  //                         [nodeId]: formItems,
  //                     }));
  //                 }
  //             }
  //         }
  //     } catch (error) {
  //         console.error('Error in handleSelectParentResult:', error);
  //     } finally {

  //     }
  // };
  // useEffect(() => {
  //     if (leadDataObj && Object.keys(leadDataObj).length) {
  //         handleSelectParentResult();
  //     }
  // }, [leadDataObj]);

  const handleSelectCampaign = async (campaign: any) => {
    if (!campaign?.proposal_id) {
      showSnackBar('Please select a campaign first', 'warning');
      setLeadDataObj((prev) => ({
        ...prev,
        campaign: null,
        webForm: null,
        retrive_fields: [],
      }));
      setLeadDataObj((prev) => ({
        ...prev,
        campaign: null,
        webForm: null,
        conditions: [],
        subGroups: [],
      }));
      return;
    }
    try {
      setFormLoading(true);
      setLeadDataObj((prev) => ({
        ...prev,
        campaign,
        webForm: null,
        conditions: [],
        subGroups: [],
      }));
    } catch (error) {
      showSnackBar('Failed to fetch forms', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  const getStatusList = async () => {
    setIsStatusLoading(true);
    try {
      if (statusList?.length > 0) return;
      if (!leadDataObj?.webForm?.leads_form_id) return;
      const res = await actions.fetchStatusAndSubStatus({
        leadFormId: leadDataObj?.webForm?.leads_form_id,
        active: true,
      });

      if (res?.status === 200) {
        const allStatuses = res?.data || [];
        setStatusList(allStatuses);
        const statusData = {};
        allStatuses?.forEach((item) => {
          if (item?.type_of_status) {
            if (!statusData[item?.type_of_status]) {
              statusData[item?.type_of_status] = [];
            }
            statusData[item.type_of_status].push(item);
          }
          item?.substatuses?.forEach((sub) => {
            const key = sub?.type_of_status || 'unknown';
            if (!statusData[key]) {
              statusData[key] = [];
            }
            statusData[key].push(sub);
          });
        });
        setTypeOfStatusList(statusData);
      }
    } catch (error) {
      console.warn('Error fetching status/substatus:', error);
    } finally {
      setIsStatusLoading(false);
    }
  };

  useEffect(() => {
    getStatusList();
  }, [leadDataObj?.webForm?.leads_form_id]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (leadDataObj?.campaign?.proposal_id) {
        await getWebFormListByCampaign(leadDataObj.campaign);
        if (!mounted) return;
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [leadDataObj?.campaign?.proposal_id]);

  useEffect(() => {
    if (leadDataObj?.webForm) {
      const selectedForm =
        webFormList?.[leadDataObj?.campaign?.proposal_id]?.[leadDataObj?.webForm?.leads_form_id];
      if (selectedForm?.leads_form_items) {
        const formItems = staticColumnsMatch(Object.values(selectedForm?.leads_form_items || []));
        setCurrentWebFormFields(formItems);
      }
      setLeadDataObj((prev) => ({
        ...prev,
        webForm: leadDataObj?.webForm,
      }));
    }
  }, [leadDataObj?.webForm, webFormList, leadDataObj?.campaign?.proposal_id]);

  return (
    <>
      <Box display="flex" alignItems="center" gap={2}>
        <CampaignAssignedDropdown
          label={
            <Typography component="span">
              Select Campaign <span style={{ color: 'red' }}>*</span>
            </Typography>
          }
          placeholder="Select Campaign"
          onChange={(value) => {
            let val = null;
            if (value && value?.proposal_id) {
              val = { proposal_id: value?.proposal_id, name: value?.name };
            }
            handleSelectCampaign(val);
          }}
          value={leadDataObj?.campaign ?? null}
          autoSelectValue={true}
          error={!!leadDataSettingErrors?.campaign}
          errorMessage={leadDataSettingErrors?.campaign ?? ''}
        />
        <SearchableDropdown
          label={
            <Typography component="span">
              Select Form <span style={{ color: 'red' }}>*</span>
            </Typography>
          }
          loading={formLoading}
          options={modifyWebFormListObj(webFormList?.[leadDataObj?.campaign?.proposal_id]) ?? []}
          labelKey="leads_form_name"
          valueKey="leads_form_id"
          value={leadDataObj?.webForm || null}
          onChange={(value) => {
            let form = null;
            if (value && value?.leads_form_items) {
              form = {
                leads_form_id: value?.leads_form_id,
                leads_form_name: value?.leads_form_name,
              };
            }
            setLeadDataObj((prev: any) => ({
              ...prev,
              webForm: form,
              retrive_fields: [],
            }));
          }}
          onOpen={async () => {
            if (leadDataObj?.campaign) {
              await getWebFormListByCampaign(leadDataObj.campaign, false);
            }
          }}
          error={!!leadDataSettingErrors?.webForm}
          errorMessage={leadDataSettingErrors?.webForm ?? ''}
        />
      </Box>

      <Box>
        <LeadQueryParameters
          group={leadDataObj}
          onGroupChange={setLeadDataObj}
          groupLabel="Where"
          groupColor="primary"
          parentResults={parentResults}
          currentWebFormFields={currentWebFormFields}
          typeOfStatusList={typeOfStatusList}
        />
      </Box>

      <Box mt={2} mb={1}>
        {/* <Typography variant="subtitle2" gutterBottom>
                    Retrieve Parameters
                </Typography> */}
        <SearchableDropdown
          options={currentWebFormFields}
          value={currentWebFormFields.filter((opt) =>
            (leadDataObj?.retrive_fields ?? []).includes(opt.key_name)
          )} // 🔹 convert stored key_names back into option objects for dropdown display
          onChange={(selectedOptions) => {
            const selectedKeys = (selectedOptions ?? []).map((opt) => opt.key_name);
            setLeadDataObj({ ...leadDataObj, retrive_fields: selectedKeys });
          }}
          label={
            <Typography>
              Retrieve Field <span style={{ color: 'red' }}>*</span>
            </Typography>
          }
          multiSelect={true}
          valueKey="key_name"
          labelKey="alias_name"
          getOptionDisabled={() => (leadDataObj?.retrive_fields ?? []).length > 1}
          error={!!leadDataSettingErrors?.retrive_fields}
          errorMessage={leadDataSettingErrors?.retrive_fields}
        />
      </Box>
    </>
  );
};

export default LeadDataSetting;
